/**
 * @module components/lead-modal
 * @requires module:common/api/roskvartal-api~RoskvartalApi
 * @requires module:common/config/url-config~UrlConfig
 * @requires module:common/errors/incorrect-phonenumber~IncorrectPhoneNumberError
 * @requires module:common/errors/invalid-full-name~InvalidFullNameError
 * @requires module:common/errors/invalid-email~InvalidEmailError
 */
import { Modal } from '@roskvartal-landings/component-modal';
import { RoskvartalApi, UrlConfig } from '@/common';
import {
  IncorrectPhoneNumberError,
  InvalidFullNameError,
  InvalidEmailError
} from '@/common/errors';

/**
 * @extends module:components/modal~Modal
 * @inheritdoc
 * @classdesc Компонент модального окна лидов
 */
class LeadModal extends Modal {
  constructor(el) {
    super(el);
    /**
     * @member {Object}
     * @property {String} fullName - ФИО пользователя
     * @property {String} phoneNumber - Номер телефона пользователя
     * @property {String} customEmail - Email пользователя
     * @property {String} comment - Комментарий к заявке
     * @property {String} clientId - ID клиента из Google Analytics
     */
    this.data = {
      fullName: null,
      phoneNumber: null,
      customEmail: null,
      clientId: null,
      comment: `Позвонить: ${this.refs.get('comment').value}`,
    };
    this.init();
  }

  /**
   * @method
   * @description Инициализация модалки
   */
  init() {
    const fullNameInput = this.refs.get('full-name');
    const phoneNumber = this.refs.get('phone-number');
    const customEmail = this.refs.get('customEmail');
    const comment = this.refs.get('comment');
    const form = this.refs.get('form');

    this.data.fullName = fullNameInput.querySelector('input').value;
    this.data.phoneNumber = phoneNumber.querySelector('input').value;
    this.data.customEmail = customEmail.querySelector('input').value;

    fullNameInput.addEventListener('input', (evt) => {
      this.data.fullName = evt.target.value;
    });
    phoneNumber.addEventListener('input', (evt) => {
      this.data.phoneNumber = evt.target.value;
    });
    customEmail.addEventListener('input', (evt) => {
      this.data.customEmail = evt.target.value;
    });
    comment.addEventListener('change', (evt) => {
      this.data.comment = `Позвонить: ${evt.target.value}`;
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.sendLead();
    });
  }

  /**
   * @method
   * @description Отправить заявку на звонок
   */
  sendLead() {
    if (RoskvartalApi.state.order.status.isPending) return;

    RoskvartalApi.mutations.SET_ACTION_STATUS_PENDING('order');

    this.refs.get('send-button').disabled = true;
    this.data.clientId = ga.getAll()[0].get('clientId');

    RoskvartalApi.order({ ...this.data, service: this.payload.service, tariff: this.payload.tariff })
      .then(LeadModal.onLeadSuccess)
      .catch((error) => {
        this.updateInvalidState(error);
        LeadModal.onLeadError(error);
      })
      .finally(() => {
        this.refs.get('send-button').disabled = false;
        LeadModal.afterLead();
      });
  }

  /**
   * @static
   * @method
   * @description Вызывает редирект на страницу "Спасибо" при успешной отправке лида
   * @param {*} response
   */
  static onLeadSuccess(response) {
    RoskvartalApi.mutations.SET_ACTION_STATUS_SUCCEEDED('order');
    const {
      data: { returnUrl },
    } = response;
    window.location = `${UrlConfig.baseRkUrl}${returnUrl}`;
  }

  /**
   * @static
   * @method
   * @description Устанавливает состояние запроса отправки лида в состояние FAILED
   */
  static onLeadError() {
    RoskvartalApi.mutations.SET_ACTION_STATUS_FAILED('order');
  }

  /**
   * @static
   * @method
   * @description Сбрасывает состояние запроса отправки лида спустя 2 секунды после завершения
   */
  static afterLead() {
    setTimeout(
      () => RoskvartalApi.mutations.RESET_ACTION_STATUS('order'),
      2000,
    );
  }

  /**
   * @method
   * @description Обновляет состояние ошибки на форме
   * @param {Error} error - Ошибка
   */
  updateInvalidState(error) {
    switch (error.constructor) {
      case InvalidFullNameError:
        this.refs
          .get('full-name')
          .querySelector('input')
          .classList.add('is-invalid');
        break;
      case IncorrectPhoneNumberError:
        this.refs
          .get('phone-number')
          .querySelector('input')
          .classList.add('is-invalid');
        break;
      case InvalidEmailError:
        this.refs
          .get('customEmail')
          .querySelector('input')
          .classList.add('is-invalid');
        break;
      default:
        break;
    }
  }
}

export default LeadModal;
